import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Tabs from '@orn/shared-web/components/Tabs';

import Entity from '../../components/entity';
import BillingSettings from '../../components/settings/BillingSettings';
import LegacySettingsForm from '../../components/settings/LegacySettingsForm';
import PersonalSettingsForm from '../../components/settings/PersonalSettingsForm';
import { useFeatureGates } from '../../hooks/useFeatureGates';

/**
 * Settings pane. Behind the `feat-onboarding-v2` gate it renders the tabbed
 * layout (the Mantle design, node `4198:95792`): a "My account" tab with the
 * Personal Information form, a "Billing" tab (a free-trial placeholder shown
 * while billing collection is not yet live), and a temporary "Legacy" tab
 * holding the old settings (the low-balance alert) until each is ported into
 * its own tab.
 * Ungated, it renders just the legacy form (the pre-redesign settings body).
 */
const SettingsPane = () => {
  const { flags, loading } = useFeatureGates();
  if (loading && flags.size === 0) {
    return null;
  }

  const tabbed = flags.has(FeatureGate.OnboardingV2);

  if (!tabbed) {
    return (
      <Entity.Pane>
        <Entity.Header title="Settings" />
        <Entity.Body primaryElement={<LegacySettingsForm />} />
      </Entity.Pane>
    );
  }

  // Tabs.Root wraps the whole pane so the tab list (rendered in the header) and
  // the tab panels (in the scrollable body) share one react-aria selection
  // context. Keeping the list in the header pins it with the title and places
  // it above the scroll-driven drop shadow instead of fighting it.
  return (
    <Tabs.Root defaultSelectedKey="my-account" className="h-full min-h-0 w-full">
      <Entity.Pane>
        <Entity.Header
          title="Settings"
          belowContent={
            <Tabs.List aria-label="Settings sections">
              <Tabs.Tab id="my-account">My account</Tabs.Tab>
              <Tabs.Tab id="billing">Billing</Tabs.Tab>
              <Tabs.Tab id="legacy">Legacy</Tabs.Tab>
            </Tabs.List>
          }
        />
        <Entity.Body
          primaryElement={
            <>
              <Tabs.Panel id="my-account" className="pt-6">
                <PersonalSettingsForm />
              </Tabs.Panel>
              <Tabs.Panel id="billing" className="pt-6">
                <BillingSettings />
              </Tabs.Panel>
              <Tabs.Panel id="legacy" className="pt-6">
                <LegacySettingsForm />
              </Tabs.Panel>
            </>
          }
        />
      </Entity.Pane>
    </Tabs.Root>
  );
};

export default SettingsPane;
